import * as React from "react"
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Input,
  Label,
  MultiTagInput,
  SearchableCombobox,
  TruncatedLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui"
import type { MultiTagInputOption } from "@nexus/ui"
import { SetNameSchema, SET_NAME_MAX, isValidYmd } from "@nexus/contracts"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import type { IndustryOption, AdPlatformOption, BrandOption, DataScope } from "@/features/opportunities/api/opportunity-sets.api"
import { competitorOptionsQueryOptions } from "@/lib/queries/opportunity-sets.queries.js"
import { tenantSettingsQueryOptions, competitorSettingsQueryOptions } from "@/lib/queries/tenant-settings.queries.js"
import { sortBrandsByVolumeDesc } from "@/features/dashboard/lib/competitor-filter-defaults"
import { DEFAULT_PRESET_ID, resolveRollingWindow } from "@/features/dashboard/lib/explore-date-presets"
import { formatCount } from "@/lib/format-number"
import { toTitleCase } from "@/lib/title-case"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { CreateSetDateRangePicker } from "../CreateSetDateRangePicker/CreateSetDateRangePicker"
import { MultiTagInputWithSelectAll } from "./MultiTagInputWithSelectAll"

export interface CreateSetFormValues {
  name: string
  industry: string
  benchmarkIndustry: string
  brand: string | null
  mainCompetitor: string | null
  industryCompetitors: string[]
  products: string[]
  adPlatforms: string | null
  strategicPriorities: { focusOn: string[]; avoid: string[] }
  brandVoice: { alwaysSay: string; neverSay: string }
  scriptGuidelines: string
  minimalGeneration: boolean
  startDate: string
  endDate: string
  activePresetId: string
}

export interface CreateSetDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onSubmit?: (values: CreateSetFormValues) => void
  isSubmitting?: boolean
  trigger?: React.ReactNode
  initialName?: string
  industries?: IndustryOption[]
  industriesLoading?: boolean
  adPlatformOptions?: AdPlatformOption[]
  adPlatformOptionsLoading?: boolean
  productOptions?: MultiTagInputOption[]
  productOptionsLoading?: boolean
  focusedProductNames?: string[]
  canUseMinimalGeneration?: boolean
  /**
   * Render fields inside a parent Dialog (Persona Opportunities drive-mode).
   * Leftover `/opportunities` keeps the default standalone Dialog.
   */
  embedded?: boolean
  /**
   * Parent accordion (unified modal): form fields, tailoring, and footer as
   * separate slots so Advanced options can sit in its own scrolling pane.
   */
  embeddedLayout?: (parts: {
    form: React.ReactNode
    advanced: React.ReactNode
    footer: React.ReactNode
  }) => React.ReactNode
  /** Shared set name from the parent unified modal. */
  embeddedName?: string
  /** Shared ad platform from the parent unified modal. */
  embeddedAdPlatform?: string | null
  /** Shared date window from the parent unified modal. */
  embeddedDate?: {
    startDate: string
    endDate: string
    activePresetId: string
  }
  /** Catalog queries — leftover defaults to `opportunities`. */
  dataScope?: DataScope
  /** Overrides the auth-store tenant for catalog queries when embedded. */
  tenantId?: string
  /** Selected My Brand, including auto-populate after an industry change. */
  onBrandChange?: (brand: string | null) => void
}

type IconName = React.ComponentProps<typeof Icon>["name"]

function CollapsibleSection({
  open,
  onToggle,
  icon,
  label,
  hint,
  badge,
  children,
}: {
  open: boolean
  onToggle: () => void
  icon: IconName
  label: string
  hint: React.ReactNode
  badge?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-border">
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors rounded-lg",
          "hover:bg-accent/50 focus-visible:outline-none focus-visible:bg-accent/50",
          open && "rounded-b-none border-b border-border",
        )}
        aria-expanded={open}
      >
        <Icon name={icon} size="sm" color="muted" className="shrink-0 self-center" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="text-sm font-semibold leading-tight text-foreground">{label}</p>
          <p className="text-xs leading-snug text-muted-foreground">{hint}</p>
        </div>
        {badge && (
          <span className="shrink-0 rounded-full bg-success-subtle px-2 py-0.5 text-xs font-medium text-success-subtle-foreground">
            {badge}
          </span>
        )}
        <Icon
          name="chevron-down"
          size="xs"
          color="muted"
          className={cn("shrink-0 transition-transform duration-200", open && "rotate-180")}
        />
      </button>
      <div
        className={`grid transition-all duration-200 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  )
}

function FieldLabel({ sign, label, positive }: { sign: string; label: string; positive?: boolean }) {
  return (
    <div className={cn(
      "flex items-center gap-1.5 text-sm font-medium",
      positive ? "text-success" : "text-error",
    )}>
      <span className="tabular-nums leading-none">{sign}</span>
      {label}
    </div>
  )
}

function SectionSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 border-t border-border" />
      <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="flex-1 border-t border-border" />
    </div>
  )
}

/** Embedded (unified modal) only: titles a cluster of competitor fields. Standalone keeps its flat list. */
function CompetitorFieldGroup({
  enabled,
  title,
  hint,
  children,
}: {
  enabled: boolean
  title: string
  hint: string
  children: React.ReactNode
}) {
  if (!enabled) return <>{children}</>
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      {children}
    </section>
  )
}

/** Embedded only: sets an industry beside the brand it scopes. */
function FieldPair({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  if (!enabled) return <>{children}</>
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">{children}</div>
}

function humanizeName(name: string): string {
  return name.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

/** Industries with no creative volume sort after every counted industry and stay disabled. */
const UNRANKED_INDUSTRY_VOLUME = -1

export function isIndustryUnranked(industry: IndustryOption): boolean {
  return industry.dataUpdated == null || industry.assetCount90d == null || industry.assetCount90d === 0
}

/** Empty when the index is missing from the catalog or the row is unranked. */
function rankedIndustryIndex(
  catalog: readonly IndustryOption[] | undefined,
  index: string | null | undefined,
): string {
  if (!catalog || !index) return ""
  const match = catalog.find((row) => row.index === index)
  if (!match || isIndustryUnranked(match)) return ""
  return match.index
}

function industryVolumeRank(industry: IndustryOption): number {
  if (isIndustryUnranked(industry)) return UNRANKED_INDUSTRY_VOLUME
  return industry.assetCount90d ?? UNRANKED_INDUSTRY_VOLUME
}

export function sortIndustriesByVolume(industries: readonly IndustryOption[]): IndustryOption[] {
  return [...industries].sort((left, right) => {
    const volumeDelta = industryVolumeRank(right) - industryVolumeRank(left)
    if (volumeDelta !== 0) return volumeDelta
    return left.title.localeCompare(right.title)
  })
}

function BrandSelectDisplay({
  loading,
  value,
  options,
  placeholder,
  loadingText,
}: {
  loading?: boolean
  value: string | null
  options: BrandOption[] | undefined
  placeholder: string
  loadingText: string
}) {
  if (loading) {
    return (
      <span className="flex w-full items-center justify-start gap-2 text-muted-foreground">
        <Icon name="loader" size="xs" className="animate-spin" />
        {loadingText}
      </span>
    )
  }
  if (value) {
    const matched = options?.find((o) => o.name === value)
    return <BrandOptionItem option={{ ...(matched ?? { name: value, imageUrl: null }), assetCount90d: undefined }} />
  }
  return <span className="w-full text-left text-muted-foreground">{placeholder}</span>
}

function BrandOptionItem({ option }: { option: BrandOption }) {
  const { t } = useTranslation()
  return (
    <span className="flex w-full items-center justify-between gap-2">
      <span className="flex min-w-0 items-center gap-2">
        {option.imageUrl ? (
          <img src={option.imageUrl} alt="" className="h-5 w-5 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="h-5 w-5 shrink-0 rounded-full bg-muted" />
        )}
        <span className="truncate">{humanizeName(option.name)}</span>
      </span>
      {option.assetCount90d != null && (
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatCount(option.assetCount90d)} {t("opportunities.assetCountCreativesLabel")}
        </span>
      )}
    </span>
  )
}

const FIELD_HINT_CLASS = "text-xs leading-snug text-muted-foreground"

const SEARCHABLE_SELECT_TRIGGER_CLASS = cn(
  "flex h-8 w-full items-center justify-between gap-2 rounded-sm border border-input-border bg-input-background px-3 py-1 text-xs text-foreground outline-none transition-colors",
  "focus:border-input-focus-border",
  "disabled:cursor-not-allowed disabled:opacity-40",
)

/** Opt-in: wider than the trigger, still capped to the viewport. Other comboboxes stay trigger-width. */
const INDUSTRY_POPOVER_CLASS =
  "max-h-72 w-auto min-w-(--radix-popover-trigger-width) max-w-[min(28rem,90vw)]"
const INDUSTRY_POPOVER_COLLISION_PADDING = 8

function IndustrySearchableCombobox({
  id,
  value,
  industries,
  onChange,
  loading,
  disabled,
  placeholder,
  loadingText,
  searchPlaceholder,
  emptyMessage,
}: {
  id: string
  value: string
  industries: IndustryOption[]
  onChange: (index: string) => void
  loading?: boolean
  disabled?: boolean
  placeholder: string
  loadingText: string
  searchPlaceholder: string
  emptyMessage: string
}) {
  const { t } = useTranslation()
  return (
    <SearchableCombobox
      items={industries}
      getKey={(ind) => ind.index}
      getLabel={(ind) => ind.title}
      value={value || null}
      onSelect={(ind) => onChange(ind.index)}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      loading={loading}
      disabled={disabled}
      contentClassName={INDUSTRY_POPOVER_CLASS}
      contentProps={{ collisionPadding: INDUSTRY_POPOVER_COLLISION_PADDING }}
      getDisabled={(ind) => isIndustryUnranked(ind)}
      renderItem={(ind, { isDisabled }) => (
        <span className="flex w-full min-w-0 items-center justify-between gap-2">
          <TruncatedLabel text={ind.title} className="min-w-0 flex-1" side="right" />
          {isDisabled ? (
            <span className="shrink-0 text-xs text-muted-foreground">
              {t("opportunities.industryNoDataLabel")}
            </span>
          ) : ind.assetCount90d != null ? (
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatCount(ind.assetCount90d)} {t("opportunities.assetCountCreativesLabel")}
            </span>
          ) : null}
        </span>
      )}
      renderTrigger={({ selected, open }) => (
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-required="true"
          aria-busy={loading || undefined}
          disabled={disabled}
          className={SEARCHABLE_SELECT_TRIGGER_CLASS}
        >
          <span className="flex min-w-0 flex-1 items-center">
            {loading ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Icon name="loader" size="xs" className="animate-spin" />
                {loadingText}
              </span>
            ) : selected ? (
              <TruncatedLabel text={selected.title} className="min-w-0 flex-1 text-left" side="bottom" />
            ) : (
              <span className="truncate text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <Icon name="chevrons-up-down" size="xs" className="shrink-0 opacity-50" aria-hidden />
        </button>
      )}
    />
  )
}

function CompetitorSearchableCombobox({
  id,
  value,
  options,
  onChange,
  loading,
  disabled,
  placeholder,
  loadingText,
  searchPlaceholder,
  emptyMessage,
  excludeName,
}: {
  id: string
  value: string | null
  options: BrandOption[] | undefined
  onChange: (name: string) => void
  loading?: boolean
  disabled?: boolean
  placeholder: string
  loadingText: string
  searchPlaceholder: string
  emptyMessage: string
  excludeName?: string | null
}) {
  const items = React.useMemo(
    () => sortBrandsByVolumeDesc((options ?? []).filter((c) => c.name !== excludeName)),
    [options, excludeName],
  )

  return (
    <SearchableCombobox
      items={items}
      getKey={(b) => b.name}
      getLabel={(b) => humanizeName(b.name)}
      value={value}
      onSelect={(b) => onChange(b.name)}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      loading={loading}
      disabled={disabled}
      contentClassName="max-h-72"
      renderTrigger={({ selected, open }) => (
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-busy={loading || undefined}
          disabled={disabled}
          className={SEARCHABLE_SELECT_TRIGGER_CLASS}
        >
          <span className="flex min-w-0 flex-1 items-center">
            <BrandSelectDisplay
              loading={loading}
              value={selected?.name ?? value}
              options={options}
              placeholder={placeholder}
              loadingText={loadingText}
            />
          </span>
          <Icon name="chevrons-up-down" size="xs" className="shrink-0 opacity-50" aria-hidden />
        </button>
      )}
      renderItem={(b) => <BrandOptionItem option={b} />}
    />
  )
}

function validate(raw: string) {
  const result = SetNameSchema.safeParse(raw)
  if (result.success) return { value: result.data, error: null }
  return { value: raw.trim(), error: result.error.issues[0]?.message ?? "Invalid name" }
}

const STEP_BADGE_CLASS =
  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] transition-[background-color,color,transform] duration-500 ease-out motion-reduce:transition-none"
const STEP_LABEL_CLASS = "text-xs transition-colors duration-500 ease-out motion-reduce:transition-none"

function StepIndicator({ step, onGoBack }: { step: 1 | 2; onGoBack: () => void }) {
  const { t } = useTranslation()
  const step1Active = step === 1
  const step1Complete = step === 2
  const step2Active = step === 2

  return (
    <div className="-mx-5 flex shrink-0 items-center gap-2 border-b border-t border-border bg-background px-5 py-3">
      <button
        type="button"
        onClick={step1Complete ? onGoBack : undefined}
        className={cn(
          "flex shrink-0 items-center gap-2",
          step1Complete ? "cursor-pointer transition-opacity hover:opacity-80" : "pointer-events-none cursor-default",
        )}
        tabIndex={step1Complete ? 0 : -1}
        aria-current={step1Active ? "step" : undefined}
      >
        <span
          className={cn(
            STEP_BADGE_CLASS,
            step1Active && "bg-primary font-semibold text-primary-foreground",
            step1Complete && "bg-primary-subtle text-primary",
          )}
        >
          {step1Complete ? (
            <Icon
              key="step-1-check"
              name="check"
              size="xs"
              className="text-primary motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-300 motion-reduce:animate-none"
            />
          ) : (
            "1"
          )}
        </span>
        <span
          className={cn(
            STEP_LABEL_CLASS,
            "font-medium",
            step1Active && "text-foreground",
            step1Complete && "text-primary",
          )}
        >
          {t("opportunities.wizardStep1Label")}
        </span>
      </button>
      <div className="relative min-w-8 h-px flex-1 overflow-hidden rounded-full bg-border" aria-hidden>
        <div
          className={cn(
            "absolute inset-0 origin-left bg-primary transition-transform duration-500 ease-out motion-reduce:transition-none",
            step1Complete ? "scale-x-100" : "scale-x-0",
          )}
        />
      </div>
      <div className="flex shrink-0 items-center gap-2" aria-current={step2Active ? "step" : undefined}>
        <span
          key={step2Active ? "step-2-active" : "step-2-inactive"}
          className={cn(
            STEP_BADGE_CLASS,
            step2Active
              ? "bg-primary font-semibold text-primary-foreground motion-safe:animate-in motion-safe:zoom-in-75 motion-safe:duration-300 motion-reduce:animate-none"
              : "bg-muted font-normal text-muted-foreground",
          )}
        >
          2
        </span>
        <span
          className={cn(
            STEP_LABEL_CLASS,
            step2Active ? "font-medium text-foreground" : "font-normal text-muted-foreground",
          )}
        >
          {t("opportunities.wizardStep2Label")}
        </span>
      </div>
    </div>
  )
}

export function CreateSetDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  trigger,
  initialName,
  industries,
  industriesLoading,
  adPlatformOptions,
  adPlatformOptionsLoading,
  productOptions,
  productOptionsLoading,
  focusedProductNames,
  canUseMinimalGeneration = false,
  embedded = false,
  embeddedLayout,
  embeddedName,
  embeddedAdPlatform,
  embeddedDate,
  dataScope = "opportunities",
  tenantId,
  onBrandChange,
}: CreateSetDialogProps) {
  const { t } = useTranslation()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const activeTenantId = useAuth((s) => s.activeTenantId)
  const catalogTenantId = tenantId ?? activeTenantId

  const { data: tenantSettings } = useQuery(tenantSettingsQueryOptions(catalogTenantId))
  const { data: competitorSettings } = useQuery(competitorSettingsQueryOptions(catalogTenantId))

  const [step, setStep] = React.useState<1 | 2>(1)
  const [name, setName] = React.useState(initialName ?? "")
  const [touched, setTouched] = React.useState(false)
  const [industry, setIndustry] = React.useState("")
  const [benchmarkIndustry, setBenchmarkIndustry] = React.useState("")
  const [brand, setBrand] = React.useState<string | null>(null)
  const [mainCompetitor, setMainCompetitor] = React.useState<string | null>(null)
  const [industryCompetitors, setIndustryCompetitors] = React.useState<string[]>([])

  const [adPlatformIdState, setAdPlatformId] = React.useState<string | null>('meta')
  const [dateSelectionState, setDateSelection] = React.useState(() => {
    const window = resolveRollingWindow(90, new Date())
    return { startDate: window.start, endDate: window.end, activePresetId: DEFAULT_PRESET_ID }
  })
  const adPlatformId = embedded && embeddedAdPlatform !== undefined
    ? (embeddedAdPlatform || null)
    : adPlatformIdState
  const dateSelection = embedded && embeddedDate ? embeddedDate : dateSelectionState
  const platformFilter = embedded && adPlatformId ? [adPlatformId] : undefined
  const activeIndustry = rankedIndustryIndex(industries, industry)
  const activeBenchmarkIndustry = rankedIndustryIndex(industries, benchmarkIndustry)

  const { data: competitorOptions, isLoading: competitorOptionsLoading } = useQuery(
    competitorOptionsQueryOptions(dataScope, catalogTenantId, activeIndustry, platformFilter),
  )
  const { data: benchmarkCompetitorOptions, isLoading: benchmarkCompetitorOptionsLoading } = useQuery(
    competitorOptionsQueryOptions(dataScope, catalogTenantId, activeBenchmarkIndustry, platformFilter),
  )
  const autoPopulatedForRef = React.useRef("")
  const benchmarkAutoPopulatedForRef = React.useRef("")
  const autoSelectedProductsForRef = React.useRef<string | null>(null)
  const didInitForOpenRef = React.useRef(false)
  /** A manual Benchmark Industry choice must survive a late My Industry default. */
  const benchmarkTouchedRef = React.useRef(false)

  const [products, setProducts] = React.useState<string[]>([])
  const [tailoringOpen, setTailoringOpen] = React.useState(false)

  const [strategicOpen, setStrategicOpen] = React.useState(false)
  const [strategicFocusOn, setStrategicFocusOn] = React.useState<string[]>([])
  const [strategicAvoid, setStrategicAvoid] = React.useState<string[]>([])
  const [brandVoiceOpen, setBrandVoiceOpen] = React.useState(false)
  const alwaysSayRef = React.useRef<HTMLTextAreaElement>(null)
  const neverSayRef = React.useRef<HTMLTextAreaElement>(null)
  const [alwaysSayFilled, setAlwaysSayFilled] = React.useState(false)
  const [neverSayFilled, setNeverSayFilled] = React.useState(false)
  const [scriptGuidelinesOpen, setScriptGuidelinesOpen] = React.useState(false)
  const scriptGuidelinesRef = React.useRef<HTMLTextAreaElement>(null)
  const scriptGuidelinesFileRef = React.useRef<HTMLInputElement>(null)
  const [scriptGuidelinesFilled, setScriptGuidelinesFilled] = React.useState(false)
  const [scriptGuidelinesUploadError, setScriptGuidelinesUploadError] = React.useState<string | null>(null)
  const [minimalGeneration, setMinimalGeneration] = React.useState(false)

  const defaultBrand = tenantSettings?.defaultBrand ?? null
  const defaultIndustry = tenantSettings?.defaultIndustry ?? null

  const sortedIndustries = React.useMemo(
    () => sortIndustriesByVolume(industries ?? []),
    [industries],
  )

  const onBrandChangeRef = React.useRef(onBrandChange)
  onBrandChangeRef.current = onBrandChange
  React.useEffect(() => {
    onBrandChangeRef.current?.(brand)
  }, [brand])

  const benchmarkCompetitorTagOptions = React.useMemo(
    () =>
      sortBrandsByVolumeDesc(
        (benchmarkCompetitorOptions ?? []).filter((c) => c.name !== brand),
      ).map((c) => ({
        id: c.name,
        value: c.name,
        label:
          c.assetCount90d != null
            ? `${humanizeName(c.name)} · ${formatCount(c.assetCount90d)} ${t("opportunities.assetCountCreativesLabel")}`
            : humanizeName(c.name),
        imageUrl: c.imageUrl ?? undefined,
      })),
    [benchmarkCompetitorOptions, brand, t],
  )

  React.useEffect(() => {
    if (industry) {
      autoPopulatedForRef.current = ""
      setBrand(defaultBrand ?? null)
    }
  }, [industry]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (benchmarkIndustry) {
      benchmarkAutoPopulatedForRef.current = ""
      setMainCompetitor(null)
      setIndustryCompetitors([])
    }
  }, [benchmarkIndustry]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (open && defaultBrand && brand === null) setBrand(defaultBrand)
  }, [open, defaultBrand]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!brand) return
    if (mainCompetitor === brand) setMainCompetitor(null)
    setIndustryCompetitors((prev) => prev.filter((c) => c !== brand))
  }, [brand]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!open || industry) return
    const rankedDefault = rankedIndustryIndex(industries, defaultIndustry)
    if (rankedDefault) setIndustry(rankedDefault)
  }, [open, defaultIndustry, industries]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!embedded || !open || benchmarkTouchedRef.current || !industry) return
    setBenchmarkIndustry((current) => current || industry)
  }, [embedded, open, industry])

  React.useEffect(() => {
    if (!open || industriesLoading || !industries) return
    setIndustry((current) => (current && !rankedIndustryIndex(industries, current) ? "" : current))
    setBenchmarkIndustry((current) => (current && !rankedIndustryIndex(industries, current) ? "" : current))
  }, [open, industries, industriesLoading])

  React.useEffect(() => {
    autoPopulatedForRef.current = ""
    benchmarkAutoPopulatedForRef.current = ""
    benchmarkTouchedRef.current = false
    setIndustry("")
    setBenchmarkIndustry("")
    setBrand(null)
    setMainCompetitor(null)
    setIndustryCompetitors([])
    setProducts(focusedProductNames ?? [])
  }, [catalogTenantId]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!open) {
      didInitForOpenRef.current = false
      setStep(1)
      return
    }
    if (didInitForOpenRef.current) return
    didInitForOpenRef.current = true

    autoPopulatedForRef.current = ""
    benchmarkAutoPopulatedForRef.current = ""
    benchmarkTouchedRef.current = false
    setStep(1)
    setName(initialName ?? "")
    setTouched(false)
    const rankedDefault = rankedIndustryIndex(industries, defaultIndustry)
    setIndustry(rankedDefault)
    setBenchmarkIndustry(embedded ? rankedDefault : "")
    setBrand(defaultBrand ?? null)
    setMainCompetitor(null)
    setIndustryCompetitors([])
    setProducts(focusedProductNames ?? [])
    if (!embedded) setAdPlatformId('meta')
    setStrategicOpen(false)
    setStrategicFocusOn([])
    setStrategicAvoid([])
    setBrandVoiceOpen(false)
    setAlwaysSayFilled(false)
    setNeverSayFilled(false)
    setScriptGuidelinesOpen(false)
    setScriptGuidelinesFilled(false)
    setScriptGuidelinesUploadError(null)
    setTailoringOpen(false)
    autoSelectedProductsForRef.current = null
    setMinimalGeneration(false)
    if (!embedded) {
      const window = resolveRollingWindow(90, new Date())
      setDateSelection({ startDate: window.start, endDate: window.end, activePresetId: DEFAULT_PRESET_ID })
    }
    if (alwaysSayRef.current) alwaysSayRef.current.value = ""
    if (neverSayRef.current) neverSayRef.current.value = ""
    if (scriptGuidelinesRef.current) scriptGuidelinesRef.current.value = ""
    requestAnimationFrame(() => { inputRef.current?.select() })
  }, [open, initialName, focusedProductNames, defaultIndustry, defaultBrand])

  React.useEffect(() => {
    if (!embedded) return
    autoPopulatedForRef.current = ""
    benchmarkAutoPopulatedForRef.current = ""
    autoSelectedProductsForRef.current = null
  }, [adPlatformId, embedded])

  // Auto-populate My Brand from My Industry options
  React.useEffect(() => {
    const populateKey = embedded ? `${adPlatformId}:${industry}` : industry
    if (!open || !industry || !competitorOptions || competitorSettings === undefined || autoPopulatedForRef.current === populateKey) return
    if (embedded && !adPlatformId) return

    const optionNames = new Set(competitorOptions.map((o) => o.name))
    setBrand(defaultBrand && optionNames.has(defaultBrand) ? defaultBrand : null)

    autoPopulatedForRef.current = populateKey
  }, [open, industry, competitorOptions, tenantSettings, competitorSettings, embedded, adPlatformId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-populate Main Competitor + Benchmark Competitors from Benchmark Industry options
  React.useEffect(() => {
    const populateKey = embedded ? `${adPlatformId}:${benchmarkIndustry}` : benchmarkIndustry
    if (!open || !benchmarkIndustry || !benchmarkCompetitorOptions || competitorSettings === undefined || benchmarkAutoPopulatedForRef.current === populateKey) return
    if (embedded && !adPlatformId) return

    const saved = competitorSettings?.find((c) => c.industry === benchmarkIndustry)
    const validNames = new Set(benchmarkCompetitorOptions.map((o) => o.name))

    if (saved?.mainCompetitor && validNames.has(saved.mainCompetitor)) {
      setMainCompetitor(saved.mainCompetitor)
    }
    if (saved?.industryCompetitors) {
      const values = saved.industryCompetitors.split(",").map((s) => s.trim()).filter((v) => v && validNames.has(v))
      if (values.length > 0) setIndustryCompetitors(values)
    } else if (embedded && benchmarkCompetitorTagOptions.length > 0) {
      setIndustryCompetitors(benchmarkCompetitorTagOptions.map((option) => option.id))
    }

    benchmarkAutoPopulatedForRef.current = populateKey
  }, [open, benchmarkIndustry, benchmarkCompetitorOptions, competitorSettings, embedded, adPlatformId]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!embedded || !open || !adPlatformId || productOptionsLoading) return
    if (autoSelectedProductsForRef.current === adPlatformId) return
    const values = (productOptions ?? [])
      .map((option) => option.value)
      .filter((value): value is string => Boolean(value))
    if (values.length === 0) return
    autoSelectedProductsForRef.current = adPlatformId
    setProducts(values)
  }, [embedded, open, adPlatformId, productOptions, productOptionsLoading])

  const resolvedName = embedded && embeddedName !== undefined ? embeddedName : name
  const { value: trimmed, error } = validate(resolvedName)
  const showError = touched && !!error
  const charCount = trimmed.length
  const nearLimit = charCount >= SET_NAME_MAX * 0.9
  const hasCommittedDatePair = isValidYmd(dateSelection.startDate) && isValidYmd(dateSelection.endDate) && dateSelection.startDate <= dateSelection.endDate
  const catalogUnlocked = !embedded || !!adPlatformId
  const canAdvanceFromStep1 = !error && !!activeIndustry && !!activeBenchmarkIndustry && !industriesLoading && !!brand && !!mainCompetitor && industryCompetitors.length > 0 && hasCommittedDatePair
  const canSubmit = canAdvanceFromStep1 && (!embedded || !!adPlatformId)

  function handleNext() {
    setTouched(true)
    if (!canAdvanceFromStep1) return
    setStep(2)
    requestAnimationFrame(() => { scrollRef.current?.scrollTo(0, 0) })
  }

  function handleBack() {
    setStep(1)
    requestAnimationFrame(() => { scrollRef.current?.scrollTo(0, 0) })
  }

  function handleCreate() {
    setTouched(true)
    if (!canSubmit) return
    onSubmit?.({
      name: trimmed,
      industry: activeIndustry,
      benchmarkIndustry: activeBenchmarkIndustry,
      brand,
      mainCompetitor,
      industryCompetitors: industryCompetitors.length > 0 ? industryCompetitors : [],
      products,
      adPlatforms: adPlatformId,
      strategicPriorities: { focusOn: strategicFocusOn, avoid: strategicAvoid },
      brandVoice: { alwaysSay: alwaysSayRef.current?.value.trim() ?? "", neverSay: neverSayRef.current?.value.trim() ?? "" },
      scriptGuidelines: scriptGuidelinesRef.current?.value.trim() ?? "",
      minimalGeneration: canUseMinimalGeneration && minimalGeneration,
      startDate: dateSelection.startDate,
      endDate: dateSelection.endDate,
      activePresetId: dateSelection.activePresetId,
    })
  }

  async function handleScriptGuidelinesFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return

    const isText = file.type === "text/plain" || file.name.toLowerCase().endsWith(".txt")
    if (!isText) {
      setScriptGuidelinesUploadError(t("opportunities.scriptGuidelinesUploadUnsupported"))
      return
    }

    try {
      const text = (await file.text()).trim()
      if (!text) return
      const target = scriptGuidelinesRef.current
      if (!target) return
      const existing = target.value.trim()
      target.value = existing ? `${existing}\n\n${text}` : text
      setScriptGuidelinesFilled(target.value.trim().length > 0)
      setScriptGuidelinesUploadError(null)
    } catch {
      setScriptGuidelinesUploadError(t("opportunities.scriptGuidelinesUploadFailed"))
    }
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (embedded) {
      handleCreate()
      return
    }
    // Step 1 navigation is explicit via the Next button — never create from the form submit path.
    if (step !== 2) return
    handleCreate()
  }

  const isBusy = isSubmitting

  const embeddedAdvancedFields = (
    <>
      <CollapsibleSection
        open={strategicOpen}
        onToggle={() => setStrategicOpen((v) => !v)}
        icon="target"
        label={t("opportunities.strategicPrioritiesLabel")}
        hint={t("opportunities.strategicPrioritiesHint")}
        badge={
          strategicFocusOn.length + strategicAvoid.length > 0
            ? t("opportunities.strategicBadge", { count: strategicFocusOn.length + strategicAvoid.length })
            : undefined
        }
      >
        <div className="flex flex-col gap-3 px-4 pb-4 pt-3">
          <div className="flex flex-col gap-1.5">
            <FieldLabel sign="+" label={t("opportunities.strategicFocusOnLabel")} positive />
            <MultiTagInput
              options={[]}
              disableDropdown
              value={strategicFocusOn}
              onChange={setStrategicFocusOn}
              placeholder={t("opportunities.strategicFocusOnPlaceholder")}
            />
            <p className={FIELD_HINT_CLASS}>{t("opportunities.strategicFocusOnHint")}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel sign="−" label={t("opportunities.strategicAvoidLabel")} />
            <MultiTagInput
              options={[]}
              disableDropdown
              value={strategicAvoid}
              onChange={setStrategicAvoid}
              placeholder={t("opportunities.strategicAvoidPlaceholder")}
            />
            <p className={FIELD_HINT_CLASS}>{t("opportunities.strategicAvoidHint")}</p>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        open={brandVoiceOpen}
        onToggle={() => setBrandVoiceOpen((v) => !v)}
        icon="message-circle"
        label={t("opportunities.brandVoiceLabel")}
        hint={
          <>
            {t("opportunities.brandVoiceHintLead")}{" "}
            <span className="font-medium text-foreground/75">
              {t("opportunities.brandVoiceHintScripts")}
            </span>
            {t("opportunities.brandVoiceHintTail")}
          </>
        }
        badge={
          alwaysSayFilled && neverSayFilled
            ? t("opportunities.brandVoiceBothSet")
            : alwaysSayFilled || neverSayFilled
              ? t("opportunities.brandVoiceOneSet")
              : undefined
        }
      >
        <div className="flex flex-col gap-3 px-4 pb-4 pt-3">
          <div className="flex flex-col gap-1.5">
            <FieldLabel sign="+" label={t("opportunities.brandVoiceAlwaysSayLabel")} positive />
            <Textarea
              ref={alwaysSayRef}
              defaultValue=""
              onChange={(e) => {
                const filled = e.target.value.trim().length > 0
                if (filled !== alwaysSayFilled) setAlwaysSayFilled(filled)
              }}
              placeholder={t("opportunities.brandVoiceAlwaysSayPlaceholder")}
              className="min-h-[72px] resize-none"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel sign="−" label={t("opportunities.brandVoiceNeverSayLabel")} />
            <Textarea
              ref={neverSayRef}
              defaultValue=""
              onChange={(e) => {
                const filled = e.target.value.trim().length > 0
                if (filled !== neverSayFilled) setNeverSayFilled(filled)
              }}
              placeholder={t("opportunities.brandVoiceNeverSayPlaceholder")}
              className="min-h-[72px] resize-none"
            />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        open={scriptGuidelinesOpen}
        onToggle={() => setScriptGuidelinesOpen((v) => !v)}
        icon="file-text"
        label={t("opportunities.scriptGuidelinesLabel")}
        hint={t("opportunities.scriptGuidelinesHint")}
        badge={scriptGuidelinesFilled ? t("opportunities.scriptGuidelinesSet") : undefined}
      >
        <div className="flex flex-col gap-2 px-4 pb-4 pt-3">
          <Textarea
            ref={scriptGuidelinesRef}
            defaultValue=""
            onChange={(e) => {
              const filled = e.target.value.trim().length > 0
              if (filled !== scriptGuidelinesFilled) setScriptGuidelinesFilled(filled)
            }}
            placeholder={t("opportunities.scriptGuidelinesPlaceholder")}
            className="min-h-[120px] resize-y"
          />
          <p className={FIELD_HINT_CLASS}>{t("opportunities.scriptGuidelinesFooter")}</p>
          <input
            ref={scriptGuidelinesFileRef}
            type="file"
            accept=".txt,text/plain"
            className="hidden"
            onChange={handleScriptGuidelinesFile}
          />
          <Button
            type="button"
            variant="outline"
            iconStart="upload"
            className="w-full border-dashed"
            onClick={() => scriptGuidelinesFileRef.current?.click()}
          >
            {t("opportunities.scriptGuidelinesUploadButton")}
          </Button>
          {scriptGuidelinesUploadError && (
            <p role="alert" className="text-xs text-destructive">
              {scriptGuidelinesUploadError}
            </p>
          )}
        </div>
      </CollapsibleSection>

      {canUseMinimalGeneration && (
        <div className="flex items-start gap-3 rounded-lg border border-dashed border-border px-4 py-3">
          <Checkbox
            id="set-minimal-generation"
            checked={minimalGeneration}
            onCheckedChange={(checked) => setMinimalGeneration(checked === true)}
            className="mt-0.5"
          />
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="set-minimal-generation" className="cursor-pointer font-medium leading-none">
              {t("opportunities.minimalGenerationLabel")}
            </Label>
            <p className={FIELD_HINT_CLASS}>{t("opportunities.minimalGenerationHint")}</p>
          </div>
        </div>
      )}
    </>
  )

  const scrollFields = (
            <>

            {(embedded || step === 1) && (
              <>
                {embedded ? null : (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <div className="flex items-center gap-0.5">
                        <Label htmlFor="set-name">{t("opportunities.setNameLabel")}</Label>
                        <span className="text-destructive text-sm" aria-hidden>*</span>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default">
                              <Icon name="info" size="xs" color="muted" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-56">
                            {t("opportunities.createSetHelperText")}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <span
                      className={`shrink-0 tabular-nums transition-colors duration-150 ${
                        showError
                          ? "text-xs text-destructive"
                          : nearLimit
                            ? "text-xs text-status-warning"
                            : FIELD_HINT_CLASS
                      }`}
                    >
                      {charCount}/{SET_NAME_MAX}
                    </span>
                  </div>
                  <Input
                    ref={inputRef}
                    id="set-name"
                    placeholder={t("opportunities.setNamePlaceholder")}
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value)
                      if (!touched) setTouched(true)
                    }}
                    onBlur={() => setTouched(true)}
                    maxLength={SET_NAME_MAX}
                    aria-invalid={showError || undefined}
                    aria-describedby={showError ? "create-set-hint" : undefined}
                    autoFocus
                  />
                  {showError ? (
                    <p id="create-set-hint" role="alert" className="text-xs text-destructive">
                      {error}
                    </p>
                  ) : null}
                </div>
                )}

                <CompetitorFieldGroup
                  enabled={embedded}
                  title={t("opportunities.yourBrandGroupTitle", "Your brand")}
                  hint={t("opportunities.yourBrandGroupHint", "Who the scripts are for")}
                >
                <FieldPair enabled={embedded}>
                {/* Industry */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-0.5">
                    <Label htmlFor="set-industry">{t("opportunities.industryLabel")}</Label>
                    <span className="text-destructive text-sm" aria-hidden>*</span>
                  </div>
                  <IndustrySearchableCombobox
                    id="set-industry"
                    value={industry}
                    industries={sortedIndustries}
                    onChange={setIndustry}
                    loading={industriesLoading}
                    disabled={industriesLoading || !catalogUnlocked}
                    placeholder={t("opportunities.industryPlaceholder")}
                    loadingText={t("common.loading")}
                    searchPlaceholder={t("opportunities.searchIndustriesPlaceholder")}
                    emptyMessage={t("opportunities.searchIndustriesEmpty")}
                  />
                </div>

                {/* My Brand */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-0.5">
                    <Label htmlFor="set-brand">{t("opportunities.settingsMyBrandLabel")}</Label>
                    <span className="text-destructive text-sm" aria-hidden>*</span>
                  </div>
                  <CompetitorSearchableCombobox
                    id="set-brand"
                    value={brand}
                    options={competitorOptions}
                    onChange={setBrand}
                    loading={competitorOptionsLoading}
                    disabled={!catalogUnlocked || !industry || competitorOptionsLoading}
                    placeholder={t("opportunities.settingsMyBrandRequired")}
                    loadingText={t("common.loading")}
                    searchPlaceholder={t("opportunities.searchBrandsPlaceholder")}
                    emptyMessage={t("opportunities.searchBrandsEmpty")}
                  />
                </div>
                </FieldPair>
                </CompetitorFieldGroup>

                <CompetitorFieldGroup
                  enabled={embedded}
                  title={t("opportunities.benchmarkGroupTitle", "Benchmark")}
                  hint={t("opportunities.benchmarkGroupHint", "Whose ads to learn from")}
                >
                <FieldPair enabled={embedded}>
                {/* Benchmark Industry */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-0.5">
                    <Label htmlFor="set-benchmark-industry">{t("opportunities.benchmarkIndustryLabel")}</Label>
                    <span className="text-destructive text-sm" aria-hidden>*</span>
                  </div>
                  <IndustrySearchableCombobox
                    id="set-benchmark-industry"
                    value={benchmarkIndustry}
                    industries={sortedIndustries}
                    onChange={(next) => {
                      benchmarkTouchedRef.current = true
                      setBenchmarkIndustry(next)
                    }}
                    loading={industriesLoading}
                    disabled={industriesLoading || !catalogUnlocked}
                    placeholder={t("opportunities.benchmarkIndustryPlaceholder")}
                    loadingText={t("common.loading")}
                    searchPlaceholder={t("opportunities.searchIndustriesPlaceholder")}
                    emptyMessage={t("opportunities.searchIndustriesEmpty")}
                  />
                </div>

                {/* Main Competitor */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className="flex items-center gap-0.5">
                      <Label htmlFor="set-main-competitor">{t("opportunities.settingsMainCompetitorLabel")}</Label>
                      <span className="text-destructive text-sm" aria-hidden>*</span>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default">
                            <Icon name="info" size="xs" color="muted" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-56">
                          {t("opportunities.settingsMainCompetitorHint")}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <CompetitorSearchableCombobox
                    id="set-main-competitor"
                    value={mainCompetitor}
                    options={benchmarkCompetitorOptions}
                    onChange={setMainCompetitor}
                    loading={benchmarkCompetitorOptionsLoading}
                    disabled={!catalogUnlocked || !benchmarkIndustry || benchmarkCompetitorOptionsLoading}
                    placeholder={t("opportunities.settingsMainCompetitorRequired")}
                    loadingText={t("common.loading")}
                    searchPlaceholder={t("opportunities.searchBrandsPlaceholder")}
                    emptyMessage={t("opportunities.searchBrandsEmpty")}
                    excludeName={brand}
                  />
                </div>
                </FieldPair>

                {/* Benchmark Competitors */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <div className="flex items-center gap-0.5">
                        <Label>{t("opportunities.settingsIndustryCompetitorsLabel")}</Label>
                        <span className="text-destructive text-sm" aria-hidden>*</span>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default">
                              <Icon name="info" size="xs" color="muted" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-56">
                            {t("opportunities.settingsIndustryCompetitorsSelectedHint")}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <span className={FIELD_HINT_CLASS}>
                      {t("opportunities.settingsIndustryCompetitorsSelectedCount", { count: industryCompetitors.length })}
                    </span>
                  </div>
                  <MultiTagInputWithSelectAll
                    data-testid="industry-competitors-input"
                    options={benchmarkCompetitorTagOptions}
                    renderTag={humanizeName}
                    disableFreeText
                    loading={benchmarkCompetitorOptionsLoading}
                    value={industryCompetitors}
                    onChange={setIndustryCompetitors}
                    placeholder={t("opportunities.settingsIndustryCompetitorsSearchPlaceholder")}
                    disabled={!catalogUnlocked || !benchmarkIndustry}
                  />
                </div>
                </CompetitorFieldGroup>

                {embedded ? null : (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-0.5">
                    <Label htmlFor="set-date-range">{t("opportunities.createSetDateRange.label", "Date range")}</Label>
                    <span className="text-destructive text-sm" aria-hidden>*</span>
                  </div>
                  <CreateSetDateRangePicker
                    id="set-date-range"
                    startDate={dateSelection.startDate}
                    endDate={dateSelection.endDate}
                    activePresetId={dateSelection.activePresetId}
                    onChange={setDateSelection}
                  />
                </div>
                )}
              </>
            )}

            {(embedded || step === 2) && (
              <>
                {/* Products */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <div className="flex items-center gap-0.5">
                        <Label htmlFor="set-products">{t("opportunities.productsLabel")}</Label>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default">
                              <Icon name="info" size="xs" color="muted" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-56">
                            {t("opportunities.productsTooltip")}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <span className={FIELD_HINT_CLASS}>
                      {t("opportunities.productsSelectedCount", { count: products.length })}
                    </span>
                  </div>
                  <MultiTagInputWithSelectAll
                    id="set-products"
                    data-testid="set-products-input"
                    options={productOptions ?? []}
                    renderTag={toTitleCase}
                    value={products}
                    onChange={setProducts}
                    loading={productOptionsLoading}
                    disabled={!catalogUnlocked}
                    placeholder={t("opportunities.productsSearchPlaceholder")}
                    noOptionsText={t("opportunities.productsNoOptions")}
                    selectedOfTotalLabel={(selected, total) =>
                      t("opportunities.productsSelectedOfTotal", { selected, total })
                    }
                    noMatchText={(query) => t("opportunities.productsNoMatch", { query })}
                  />
                  <p className={FIELD_HINT_CLASS}>{t("opportunities.productsHint")}</p>
                </div>

                {embedded && !embeddedLayout ? (
                  <CollapsibleSection
                    open={tailoringOpen}
                    onToggle={() => setTailoringOpen((open) => !open)}
                    icon="settings"
                    label={t("personaOpportunities.createDialog.advancedLabel")}
                    hint={t("opportunities.embeddedAdvancedHint")}
                  >
                    <div className="flex flex-col gap-4 p-4">{embeddedAdvancedFields}</div>
                  </CollapsibleSection>
                ) : !embedded ? (
                  <>
                {/* Ad Platforms */}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="set-ad-platform">{t("opportunities.adPlatformsLabel")}</Label>
                  <Select
                    value={adPlatformId ?? ""}
                    onValueChange={(v) => setAdPlatformId(v || null)}
                    disabled={adPlatformOptionsLoading}
                  >
                    <SelectTrigger id="set-ad-platform" aria-busy={adPlatformOptionsLoading || undefined}>
                      {adPlatformOptionsLoading ? (
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <Icon name="loader" size="xs" className="animate-spin" />
                          {t("common.loading")}
                        </span>
                      ) : (
                        <SelectValue placeholder={t("opportunities.adPlatformPlaceholder")} />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {(adPlatformOptions ?? []).map((platform) => (
                        <SelectItem key={platform.id} value={platform.id}>
                          {platform.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <SectionSeparator label={t("opportunities.shapesOpportunitiesLabel")} />

                {/* Strategic priorities */}
                <CollapsibleSection
                  open={strategicOpen}
                  onToggle={() => setStrategicOpen((v) => !v)}
                  icon="target"
                  label={t("opportunities.strategicPrioritiesLabel")}
                  hint={t("opportunities.strategicPrioritiesHint")}
                  badge={
                    strategicFocusOn.length + strategicAvoid.length > 0
                      ? t("opportunities.strategicBadge", { count: strategicFocusOn.length + strategicAvoid.length })
                      : undefined
                  }
                >
                  <div className="flex flex-col gap-3 px-4 pb-4 pt-3">
                    <div className="flex flex-col gap-1.5">
                      <FieldLabel sign="+" label={t("opportunities.strategicFocusOnLabel")} positive />
                      <MultiTagInput
                        options={[]}
                        disableDropdown
                        value={strategicFocusOn}
                        onChange={setStrategicFocusOn}
                        placeholder={t("opportunities.strategicFocusOnPlaceholder")}
                      />
                      <p className={FIELD_HINT_CLASS}>{t("opportunities.strategicFocusOnHint")}</p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <FieldLabel sign="−" label={t("opportunities.strategicAvoidLabel")} />
                      <MultiTagInput
                        options={[]}
                        disableDropdown
                        value={strategicAvoid}
                        onChange={setStrategicAvoid}
                        placeholder={t("opportunities.strategicAvoidPlaceholder")}
                      />
                      <p className={FIELD_HINT_CLASS}>{t("opportunities.strategicAvoidHint")}</p>
                    </div>
                  </div>
                </CollapsibleSection>

                <SectionSeparator label={t("opportunities.shapesScriptsLabel")} />

                {/* Brand voice */}
                <CollapsibleSection
                  open={brandVoiceOpen}
                  onToggle={() => setBrandVoiceOpen((v) => !v)}
                  icon="message-circle"
                  label={t("opportunities.brandVoiceLabel")}
                  hint={
                    <>
                      {t("opportunities.brandVoiceHintLead")}{" "}
                      <span className="font-medium text-foreground/75">
                        {t("opportunities.brandVoiceHintScripts")}
                      </span>
                      {t("opportunities.brandVoiceHintTail")}
                    </>
                  }
                  badge={
                    alwaysSayFilled && neverSayFilled
                      ? t("opportunities.brandVoiceBothSet")
                      : alwaysSayFilled || neverSayFilled
                        ? t("opportunities.brandVoiceOneSet")
                        : undefined
                  }
                >
                  <div className="flex flex-col gap-3 px-4 pb-4 pt-3">
                    <div className="flex flex-col gap-1.5">
                      <FieldLabel sign="+" label={t("opportunities.brandVoiceAlwaysSayLabel")} positive />
                      <Textarea
                        ref={alwaysSayRef}
                        defaultValue=""
                        onChange={(e) => {
                          const filled = e.target.value.trim().length > 0
                          if (filled !== alwaysSayFilled) setAlwaysSayFilled(filled)
                        }}
                        placeholder={t("opportunities.brandVoiceAlwaysSayPlaceholder")}
                        className="min-h-[72px] resize-none"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <FieldLabel sign="−" label={t("opportunities.brandVoiceNeverSayLabel")} />
                      <Textarea
                        ref={neverSayRef}
                        defaultValue=""
                        onChange={(e) => {
                          const filled = e.target.value.trim().length > 0
                          if (filled !== neverSayFilled) setNeverSayFilled(filled)
                        }}
                        placeholder={t("opportunities.brandVoiceNeverSayPlaceholder")}
                        className="min-h-[72px] resize-none"
                      />
                    </div>
                  </div>
                </CollapsibleSection>

                {/* Script guidelines */}
                <CollapsibleSection
                  open={scriptGuidelinesOpen}
                  onToggle={() => setScriptGuidelinesOpen((v) => !v)}
                  icon="file-text"
                  label={t("opportunities.scriptGuidelinesLabel")}
                  hint={t("opportunities.scriptGuidelinesHint")}
                  badge={scriptGuidelinesFilled ? t("opportunities.scriptGuidelinesSet") : undefined}
                >
                  <div className="flex flex-col gap-2 px-4 pb-4 pt-3">
                    <Textarea
                      ref={scriptGuidelinesRef}
                      defaultValue=""
                      onChange={(e) => {
                        const filled = e.target.value.trim().length > 0
                        if (filled !== scriptGuidelinesFilled) setScriptGuidelinesFilled(filled)
                      }}
                      placeholder={t("opportunities.scriptGuidelinesPlaceholder")}
                      className="min-h-[120px] resize-y"
                    />
                    <p className={FIELD_HINT_CLASS}>{t("opportunities.scriptGuidelinesFooter")}</p>
                    <input
                      ref={scriptGuidelinesFileRef}
                      type="file"
                      accept=".txt,text/plain"
                      className="hidden"
                      onChange={handleScriptGuidelinesFile}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      iconStart="upload"
                      className="w-full border-dashed"
                      onClick={() => scriptGuidelinesFileRef.current?.click()}
                    >
                      {t("opportunities.scriptGuidelinesUploadButton")}
                    </Button>
                    {scriptGuidelinesUploadError && (
                      <p role="alert" className="text-xs text-destructive">
                        {scriptGuidelinesUploadError}
                      </p>
                    )}
                  </div>
                </CollapsibleSection>

                {canUseMinimalGeneration && (
                  <div className="flex items-start gap-3 rounded-lg border border-dashed border-border px-4 py-3">
                    <Checkbox
                      id="set-minimal-generation"
                      checked={minimalGeneration}
                      onCheckedChange={(checked) => setMinimalGeneration(checked === true)}
                      className="mt-0.5"
                    />
                    <div className="flex flex-col gap-0.5">
                      <Label htmlFor="set-minimal-generation" className="cursor-pointer font-medium leading-none">
                        {t("opportunities.minimalGenerationLabel")}
                      </Label>
                      <p className={FIELD_HINT_CLASS}>{t("opportunities.minimalGenerationHint")}</p>
                    </div>
                  </div>
                )}
                  </>
                ) : null}
              </>
            )}
            </>
  )

  const footer = (
          <DialogFooter className={embedded ? "shrink-0" : "shrink-0 sm:justify-between"}>
            {embedded ? (
              <>
                <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={isBusy}>
                  {t("common.cancel")}
                </Button>
                <Button
                  type="button"
                  data-testid="persona-set-generate"
                  iconStart="sparkles"
                  onClick={handleCreate}
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting
                    ? t("personaOpportunities.createDialog.generating")
                    : t("personaOpportunities.createDialog.generate")}
                </Button>
              </>
            ) : step === 1 ? (
              <>
                <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={isBusy}>
                  {t("common.cancel")}
                </Button>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleNext()
                  }}
                  disabled={!canAdvanceFromStep1}
                  iconEnd="chevron-right"
                >
                  {t("opportunities.wizardNext")}
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="ghost" onClick={handleBack} disabled={isBusy} iconStart="chevron-left">
                  {t("opportunities.wizardBack")}
                </Button>
                <Button
                  type="button"
                  onClick={handleCreate}
                  disabled={!canAdvanceFromStep1 || isSubmitting}
                  iconStart="sparkles"
                >
                  {t("opportunities.createAndGenerate")}
                </Button>
              </>
            )}
          </DialogFooter>
  )

  const formBody = (
        <form onSubmit={handleFormSubmit} className="flex min-h-0 flex-1 flex-col">
          {embedded && embeddedLayout ? (
            embeddedLayout({
              form: scrollFields,
              advanced: embeddedAdvancedFields,
              footer,
            })
          ) : (
            <>
              {embedded ? null : (
                <DialogHeader className="shrink-0 pb-4">
                  <DialogTitle>{t("opportunities.createSet")}</DialogTitle>
                  <DialogDescription className="text-sm">{t("opportunities.createSetDescription")}</DialogDescription>
                </DialogHeader>
              )}
              {embedded ? null : <StepIndicator step={step} onGoBack={handleBack} />}
              <div ref={scrollRef} className="flex flex-1 flex-col gap-4 overflow-y-auto py-4 pr-1 -mr-1">
                {scrollFields}
              </div>
              {footer}
            </>
          )}
        </form>
  )

  if (embedded) {
    return formBody
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (isBusy) return; onOpenChange?.(next) }}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        variant="form"
        className="flex h-[90vh] max-h-[90vh] w-full flex-col sm:max-w-xl"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {formBody}
      </DialogContent>
    </Dialog>
  )
}
